import { Link, useParams } from "react-router-dom";

const CONTACT_PHONE = "828-424-4828";
const CONTACT_EMAIL = "Halegalcannabis@gmail.com";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    blocks: [
      { type: "p", text: "We are committed to protecting your privacy in compliance with applicable laws, including Texas consumer protection regulations. Note that as of 2026, hemp-derived products (typically under 0.3% Delta-9 THC) are regulated under federal and state guidelines, but we do not collect or process sensitive health data beyond what's necessary for age verification and transactions." },
      { type: "p", text: "By using our Services, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use our Services." },

      { type: "h", text: "1. Information We Collect" },
      { type: "p", text: "We collect the following types of information:" },
      { type: "sub", text: "Information You Provide Directly" },
      { type: "ul", items: [
        "Name, email address, phone number, shipping/billing address when placing orders, creating an account, or contacting us.",
        "Payment information (handled securely by third-party processors; we do not store full card details).",
        "Date of birth or age verification details (required for compliance with age restrictions—must be 21+ for purchases).",
        "Government-issued ID information (e.g., driver's license number, expiration) for in-person age verification where required by law or policy.",
        "Any messages, inquiries, or feedback you submit.",
      ]},
      { type: "sub", text: "Automatically Collected Information" },
      { type: "ul", items: [
        "Device and browser details (IP address, browser type, operating system).",
        "Usage data (pages visited, time spent, referring site).",
        "Cookies and similar technologies for site functionality, analytics, and preferences (see our Cookie Policy section below).",
      ]},
      { type: "sub", text: "In-Store Information" },
      { type: "ul", items: [
        "Security camera footage in and around our retail location for safety and loss prevention.",
        "Purchase history and transaction details.",
      ]},
      { type: "p", text: "We do not intentionally collect sensitive personal information (e.g., health data, precise geolocation beyond IP-derived area, or biometric data) unless directly provided for a specific purpose and required for compliance." },

      { type: "h", text: "2. How We Use Your Information" },
      { type: "p", text: "We use collected information to:" },
      { type: "ul", items: [
        "Process and fulfill orders, including shipping and payment.",
        "Verify age and eligibility to purchase restricted products.",
        "Provide customer service, respond to inquiries, and handle refunds/exchanges per our Refund Policy.",
        "Improve our Site, products, and services (e.g., analytics on usage patterns).",
        "Send transactional emails (order confirmations, shipping updates).",
        "Comply with legal obligations, prevent fraud, enforce our policies, or respond to subpoenas/law enforcement requests.",
        "With your consent, send promotional offers or newsletters (you can opt out anytime).",
      ]},

      { type: "h", text: "3. How We Share Your Information" },
      { type: "p", text: "We do not sell your personal information. We may share it with:" },
      { type: "ul", items: [
        "Service providers (e.g., payment processors like Stripe/PayPal, shipping carriers like USPS/UPS, email services, website hosting/analytics tools like Google Analytics).",
        "Legal authorities if required by law, court order, or to protect our rights/safety.",
        "In the event of a business transfer (e.g., merger, acquisition), your information may transfer to the new entity.",
      ]},

      { type: "h", text: "4. Data Security" },
      { type: "p", text: "We implement reasonable administrative, technical, and physical safeguards to protect your information from unauthorized access, loss, or misuse. However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security." },

      { type: "h", text: "5. Cookies and Tracking Technologies" },
      { type: "p", text: "We use cookies, pixels, and similar tools for essential functions (e.g., cart persistence), performance (analytics), and marketing. You can manage preferences via your browser settings or our cookie banner (if implemented). Third-party tools (e.g., Google Analytics) may collect data; see their policies for details." },

      { type: "h", text: "6. Your Rights and Choices" },
      { type: "p", text: "Depending on applicable laws:" },
      { type: "ul", items: [
        "Access, correct, or delete your personal information by contacting us.",
        "Opt out of marketing emails (unsubscribe link in emails or contact us).",
        "Limit certain automated tracking (browser Do Not Track signals may not be fully honored).",
      ]},
      { type: "p", text: "For Texas residents, we aim to handle requests reasonably in line with applicable Texas privacy regulations." },

      { type: "h", text: "7. Retention" },
      { type: "p", text: "We retain information as long as needed for the purposes described (e.g., transaction records for accounting/tax, typically 7 years) or as required by law, then securely delete or anonymize it." },

      { type: "h", text: "8. Third-Party Links" },
      { type: "p", text: "Our Site may link to third-party sites (e.g., social media, payment gateways). We are not responsible for their privacy practices—review their policies." },

      { type: "h", text: "9. Changes to This Policy" },
      { type: "p", text: "We may update this Privacy Policy. Changes will be posted here with an updated \"Last Updated\" date. Continued use after changes constitutes acceptance." },
    ],
  },

  refund: {
    title: "Refund Policy",
    blocks: [
      { type: "p", text: `This Refund Policy applies to all purchases made from High Achievers (the "Company," "we," "us," or "our"), including in-store purchases and any online or delivery services for hemp-derived cannabinoid products, accessories, devices, or related items in compliance with applicable Texas laws.` },
      { type: "p", text: "We strive for customer satisfaction, but due to the nature of cannabis/hemp products (including regulations around consumables, hygiene, and potential impairment), our refund policy is limited. All decisions regarding refunds or exchanges are at our sole discretion and subject to verification." },

      { type: "h", text: "1. Eligible Items for Refund – Devices and Non-Consumable Products" },
      { type: "p", text: "If any device (e.g., vaporizers, batteries, grinders, pipes, or other hardware/accessories) or non-consumable item is defective, damaged, or broken upon receipt or use:" },
      { type: "ul", items: [
        "You may request a full refund or exchange.",
        "You must return the item in person to our store location within 3 days (72 hours) of the original purchase date/time.",
        "Proof of purchase (receipt or digital record) is required.",
        "The item must be returned in its original condition/packaging (where applicable), with any defects clearly demonstrable.",
        "We will inspect the item to confirm the issue (e.g., manufacturing defect or breakage not caused by misuse).",
        "If approved, refunds will be issued to the original payment method.",
      ]},

      { type: "h", text: "2. Non-Refundable Items" },
      { type: "p", text: "The following products are non-refundable and non-exchangeable under any circumstances (including defects, dissatisfaction, or change of mind):" },
      { type: "ul", items: [
        "All flower (bud, hemp flower, etc.)",
        "All pre-rolls",
        "All edibles (gummies, chocolates, beverages, etc.)",
        "Any other consumable hemp-derived cannabinoid products (e.g., tinctures, capsules, topicals, unless specifically noted otherwise)",
      ]},
      { type: "p", text: "This policy exists due to health, safety, and regulatory requirements surrounding consumable cannabis/hemp products." },

      { type: "h", text: "Questions or Concerns?" },
      { type: "p", text: `If you have questions about this Refund Policy or need to request a refund for an eligible item, please contact us at: ${CONTACT_EMAIL} or ${CONTACT_PHONE}.` },
      { type: "p", text: "For in-store returns, please visit us at our Texarkana, Texas location with your receipt and the item in question." },
    ],
  },

  sales: {
    title: "Sales Policy",
    blocks: [
      { type: "sub", text: "Return & Refund Policy" },
      { type: "h", text: "All Sales Final Policy" },
      { type: "p", text: "All sales are final. No returns, refunds, or exchanges." },
      { type: "ul", items: [
        "All sales made through High Achievers, whether in-store or online, are final.",
        "We do not accept returns, refunds, or exchanges for any products once a purchase has been completed.",
        "Please review your order carefully before completing your purchase. By completing a transaction, you acknowledge and agree to this policy.",
        "This policy applies to all products, including promotional items and discounted merchandise.",
        "Exceptions may only be made if required by law.",
      ]},
      { type: "h", text: "Important Notice" },
      { type: "p", text: "By making a purchase on our website or in our stores, you confirm that you have read, understood, and agree to be bound by this Sales Policy. If you do not agree with this policy, please do not complete your purchase." },
    ],
  },

  compliance: {
    title: "Compliance Policy",
    updated: "Last Updated: January 2025",
    blocks: [
      { type: "h", text: "1. Scope and Commitment" },
      { type: "p", text: "High Achievers is dedicated to operating lawfully and responsibly. We will:" },
      { type: "ul", items: [
        "Comply with the 2018 Farm Bill and applicable federal laws.",
        "Adhere to Texas statutes (e.g., Texas Health & Safety Code Chapter 443 on consumable hemp products).",
        "Implement internal controls for age verification, product safety, accurate labeling, and record-keeping.",
        "Train all employees on compliance requirements annually or upon material changes.",
      ]},

      { type: "h", text: "2. Age Verification and Sales Restrictions" },
      { type: "ul", items: [
        "All purchasers must be at least 21 years of age.",
        "We require valid government-issued photo ID (e.g., driver's license) for every in-person or delivery transaction involving restricted products.",
        "We will not sell to anyone appearing under 21 or unable to provide valid ID.",
        "No sales to minors; violations may result in immediate termination and reporting to authorities.",
        "Products are for personal use only by adults; resale or distribution prohibited.",
      ]},

      { type: "h", text: "3. Product Compliance" },
      { type: "ul", items: [
        "All products are hemp-derived and represented as containing no more than 0.3% Delta-9 THC on a dry weight basis (current federal/state standard).",
        "We source from suppliers providing Certificates of Analysis (COAs) from ISO-accredited labs confirming compliance, including potency, contaminants (pesticides, heavy metals, microbes), and no prohibited synthetics.",
        "No medical claims: Products are not intended to diagnose, treat, cure, or prevent any disease.",
      ]},

      { type: "h", text: "4. Labeling and Packaging Requirements" },
      { type: "p", text: "All products include:" },
      { type: "ul", items: [
        "Accurate ingredient list, cannabinoid profile, and potency (mg per serving/total).",
        `"Contains hemp-derived cannabinoids. For use by adults 21+ only."`,
        `Warnings: "Keep out of reach of children and pets." "May cause impairment—do not operate vehicles/machinery."`,
        "QR code/link to COA.",
        "Child-resistant packaging for edibles, pre-rolls, and other consumables.",
        "Labels comply with FTC guidelines (no false advertising) and any applicable state/federal standards.",
      ]},

      { type: "h", text: "5. Testing and Quality Assurance" },
      { type: "ul", items: [
        "Third-party lab testing required for potency, contaminants, and compliance before sale.",
        "Records of COAs maintained for at least 2 years (or longer if required).",
        "Any non-compliant product immediately quarantined, removed from sale, and reported/disposed per law.",
      ]},

      { type: "h", text: "6. Record-Keeping and Tracking" },
      { type: "ul", items: [
        "Maintain detailed records of purchases, sales, inventory, suppliers, and testing for audit purposes.",
        "If state tracking system implemented, we will participate fully.",
      ]},

      { type: "h", text: "7. Advertising and Marketing" },
      { type: "ul", items: [
        "No targeting minors or implying medical benefits.",
        `All ads include "21+ only" disclaimers.`,
        "Compliant with platform rules.",
      ]},

      { type: "h", text: "8. Enforcement and Violations" },
      { type: "ul", items: [
        "Internal audits conducted regularly.",
        "Violations of this policy result in disciplinary action up to termination.",
      ]},

      { type: "h", text: "9. Monitoring and Updates" },
      { type: "p", text: `This policy is reviewed at least quarterly or upon legal changes. Updates posted with new "Last Updated" date.` },
    ],
  },

  terms: {
    title: "Terms and Conditions",
    blocks: [
      { type: "p", text: "Welcome to High Achievers. By accessing or using our website, purchasing our products, or visiting our retail location, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our Services." },

      { type: "h", text: "1. Eligibility (21+)" },
      { type: "p", text: "You must be at least 21 years of age and reside in a jurisdiction where hemp-derived products are legal to purchase from us. By using our Services, you represent and warrant that you meet these requirements. We may require valid government-issued photo ID to verify your age at any time." },

      { type: "h", text: "2. Products and Compliance" },
      { type: "p", text: "All products offered are hemp-derived and represented as containing no more than 0.3% Delta-9 THC on a dry weight basis, in accordance with the 2018 Farm Bill and applicable Texas law. Products are for personal, adult use only. Resale or redistribution is prohibited." },

      { type: "h", text: "3. No Medical Claims" },
      { type: "p", text: "Statements made on this site have not been evaluated by the Food and Drug Administration (FDA). Our products are not intended to diagnose, treat, cure, or prevent any disease. Consult a physician before use, especially if you are pregnant, nursing, taking medication, or have a medical condition." },

      { type: "h", text: "4. Orders, Pricing, and Payment" },
      { type: "ul", items: [
        "All orders are subject to acceptance and availability.",
        "Prices are shown in U.S. dollars and may change without notice.",
        "Payment is processed securely by third-party processors; we do not store full card details.",
        "We reserve the right to refuse or cancel any order at our discretion, including for suspected fraud or ineligibility.",
      ]},

      { type: "h", text: "5. Shipping and Local Pickup" },
      { type: "p", text: "We ship hemp-derived products only to U.S. states where their sale is permitted. Restrictions vary by product and may change as state law evolves. Local pickup may be available at our Texarkana, Texas location. Title and risk of loss pass to you upon delivery to the carrier." },

      { type: "h", text: "6. Sales, Returns, and Refunds" },
      { type: "p", text: "Please review our Sales Policy and Refund Policy, which are incorporated into these Terms. Consumable products are non-refundable due to health, safety, and regulatory requirements." },

      { type: "h", text: "7. Intellectual Property" },
      { type: "p", text: "All content on this site—including logos, text, graphics, and images—is the property of High Achievers or its licensors and is protected by applicable intellectual property laws. You may not use, reproduce, or distribute it without written permission." },

      { type: "h", text: "8. Limitation of Liability" },
      { type: "p", text: "To the fullest extent permitted by law, High Achievers is not liable for any indirect, incidental, or consequential damages arising from the use or misuse of our products or Services. Products are provided \"as-is\" with all disclaimers required by law." },

      { type: "h", text: "9. Governing Law" },
      { type: "p", text: "These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law principles. Any disputes shall be resolved in the state or federal courts located in Texas." },

      { type: "h", text: "10. Changes to These Terms" },
      { type: "p", text: `We may update these Terms at any time. Changes are effective when posted with an updated "Last Updated" date. Continued use of our Services constitutes acceptance of the revised Terms.` },

      { type: "h", text: "11. Contact" },
      { type: "p", text: `Questions about these Terms? Contact us at ${CONTACT_EMAIL} or ${CONTACT_PHONE}.` },
    ],
  },
};

function PolicyBlock({ block }) {
  if (block.type === "h") {
    return <h2 className="font-serif text-2xl sm:text-3xl text-sand mt-12 mb-4 leading-tight">{block.text}</h2>;
  }
  if (block.type === "sub") {
    return <h3 className="text-sm uppercase tracking-[0.2em] text-copper mt-8 mb-3">{block.text}</h3>;
  }
  if (block.type === "ul") {
    return (
      <ul className="my-4 space-y-2.5 list-disc pl-6 text-base sm:text-lg text-sand/85 leading-relaxed">
        {block.items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    );
  }
  return <p className="my-4 text-base sm:text-lg text-sand/85 leading-relaxed">{block.text}</p>;
}

export default function Policies() {
  const { slug } = useParams();
  const policy = POLICIES[slug];
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20" data-testid={`policy-${slug}`}>
      {policy ? (
        <>
          <span className="eyebrow">Policies</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-sand mt-3 leading-tight" data-testid="policy-title">{policy.title}</h1>
          {policy.updated && <div className="mt-3 text-sm text-sand/55">{policy.updated}</div>}
          <div className="divider my-6" />
          <div>
            {policy.blocks.map((b, i) => <PolicyBlock key={i} block={b} />)}
          </div>
          <div className="mt-14 pt-8 border-t border-copper/20 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.18em] text-sand/60">
            {Object.entries(POLICIES).filter(([k]) => k !== slug).map(([k, p]) => (
              <Link key={k} to={`/policies/${k}`} className="hover:text-copper">{p.title}</Link>
            ))}
          </div>
        </>
      ) : (
        <>
          <h1 className="font-serif text-4xl text-sand">Policy not found</h1>
          <Link to="/" className="mt-6 inline-block text-copper underline">Back home</Link>
        </>
      )}
    </div>
  );
}
